import { useMutation, useQuery } from "@tanstack/react-query";
import type {
	DocumentProgress,
	EmbedCode,
	StoredDocumentProgress,
	UserSettings,
	UserSettingsPatch,
} from "../../domain/index.ts";
import {
	createEmbedCode,
	getDocumentProgress,
	getUserSettings,
	saveDocumentProgress,
	saveUserSettings,
} from "./api.ts";

export const useEmbedCode = (code: string) =>
	useQuery<EmbedCode>({
		queryKey: ["embed-code", code],
		queryFn: () => createEmbedCode(code),
		enabled: Boolean(code),
		staleTime: 5 * 60 * 1000,
	});

export const useEmbedMutation = () =>
	useMutation<EmbedCode, Error, { code: string }>({
		mutationFn: ({ code }) => createEmbedCode(code),
	});

export const useUserSettings = () =>
	useQuery<UserSettings | null>({
		queryKey: ["user-settings"],
		queryFn: () => getUserSettings(),
	});

export const useSaveUserSettingsMutation = () =>
	useMutation<UserSettings | null, Error, { userSettings: UserSettingsPatch }>({
		mutationFn: ({ userSettings }) => saveUserSettings(userSettings),
	});

export const useDocumentProgress = (documentId: string | undefined) =>
	useQuery<DocumentProgress>({
		queryKey: ["document-progress", documentId],
		// biome-ignore lint/style/noNonNullAssertion: `enabled` below guarantees the query only runs with a documentId.
		queryFn: () => getDocumentProgress(documentId!),
		enabled: Boolean(documentId),
	});

export const useDocumentProgressMutation = (documentId: string) =>
	useMutation<StoredDocumentProgress | null, Error, { progress: number }>({
		mutationFn: ({ progress }) => saveDocumentProgress(documentId, progress),
	});
